import type {
  AskBreakdownResult,
  AskExecutableResult,
  AskFilterResult,
  AskAggregateResult,
  AskResult,
  AskTimelineResult,
} from './ask.js'
import type { QuerySpec } from './query.js'

/** Everything a `compare` may hold: the QuerySpec union minus `compare` itself. */
type LeafQuerySpec = Exclude<QuerySpec, { kind: 'compare' }>

/**
 * Turns a classified `AskResult` into the `QuerySpec` Cadence's
 * `/query/execute` consumes.
 *
 * The two shapes are near-identical by design — the Ask spec's result kinds
 * were drawn from the query contract — so this is mostly a widening cast. It
 * exists as a real function anyway for the one place they genuinely diverge,
 * and so that divergence has somewhere to be written down instead of being
 * discovered later in a network trace.
 *
 * ── THE ONE DIVERGENCE ────────────────────────────────────────────────────
 * `AskBreakdownResult.orderByField` has no counterpart in the contract's
 * `BreakdownQuerySpec`. The query engine's breakdown sums `Run.amount` — the
 * single per-run money field — and offers no way to name a different field to
 * sum. So an Ask result asking to rank by the sum of, say, `quantity` cannot
 * be expressed to the engine.
 *
 * This drops the field rather than silently reinterpreting it, and
 * `askBreakdownOrderFieldUnsupported()` below lets the caller detect the case
 * *before* executing, so it can refuse honestly instead of returning a ranking
 * ordered by something the operator didn't ask for. Flagged for joint review:
 * either the contract grows the field or the Ask schema loses it.
 */
export function askResultToQuerySpec(result: AskExecutableResult): QuerySpec {
  switch (result.kind) {
    case 'filter':
      return filterSpec(result)
    case 'aggregate':
      return aggregateSpec(result)
    case 'breakdown':
      return breakdownSpec(result)
    case 'timeline':
      return timelineSpec(result)
    case 'compare':
      return {
        kind: 'compare',
        queries: result.queries.map((q) => {
          switch (q.kind) {
            case 'filter':
              return filterSpec(q)
            case 'aggregate':
              return aggregateSpec(q)
            case 'breakdown':
              return breakdownSpec(q)
          }
        }),
      }
  }
}

function filterSpec(r: AskFilterResult): LeafQuerySpec {
  return { kind: 'filter', filter: r.filter, ...(r.limit === undefined ? {} : { limit: r.limit }) }
}

function aggregateSpec(r: AskAggregateResult): LeafQuerySpec {
  return {
    kind: 'aggregate',
    filter: r.filter,
    function: r.function,
    ...(r.field === undefined ? {} : { field: r.field }),
    ...(r.groupBy === undefined ? {} : { groupBy: r.groupBy }),
  }
}

function breakdownSpec(r: AskBreakdownResult): LeafQuerySpec {
  // `orderByField` deliberately not forwarded — see the divergence note above.
  return {
    kind: 'breakdown',
    filter: r.filter,
    groupBy: r.groupBy,
    orderBy: r.orderBy,
    ...(r.topN === undefined ? {} : { topN: r.topN }),
  }
}

function timelineSpec(r: AskTimelineResult): LeafQuerySpec {
  return { kind: 'timeline', filter: r.filter, granularity: r.granularity }
}

/**
 * True when a breakdown names a field to sum that the query engine cannot
 * honour. `total_amount` (the run amount) is the only sum the engine computes,
 * so any other named field would produce a ranking ordered by something other
 * than what was asked for — which is worse than refusing.
 */
const ENGINE_SUMMABLE_FIELDS = new Set(['amount', 'total_amount', 'total'])

export function askBreakdownOrderFieldUnsupported(result: AskBreakdownResult): boolean {
  if (result.orderBy !== 'sum') return false
  const field = result.orderByField
  return field !== undefined && !ENGINE_SUMMABLE_FIELDS.has(field)
}

/** Whether this result is something Cadence can run, as opposed to a routing decision or a refusal. */
export function isExecutableAskResult(result: AskResult): result is AskExecutableResult {
  return result.kind !== 'route_to_view' && result.kind !== 'cannot_answer'
}
